import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import "./styles/FnpWaterModule.css";

// Charts
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
  Title,
} from "chart.js";
import { Line, Bar } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
  Title
);

// ---------- Config ----------
const API = {
  mastersites: "/api/mastersites",
  sites: "/api/sites",
  fnp: "/api/fnp",
  summary: "/api/fnp/summary",
};

const WATER_SOURCES = [
  { value: "piped", label: "Piped (Tap) Water" },
  { value: "borewell", label: "Borewell / Handpump" },
  { value: "protected_well", label: "Protected Well" },
  { value: "unprotected", label: "Unprotected Source" },
  { value: "tanker", label: "Tanker" },
  { value: "surface", label: "Surface Water (river/pond)" },
  { value: "other", label: "Other" },
];

const TREAT_METHODS = [
  { value: "none", label: "No Treatment" },
  { value: "boil", label: "Boiling" },
  { value: "filter", label: "Filter (non-RO)" },
  { value: "chlorine", label: "Chlorination" },
  { value: "uv_ro", label: "UV/RO" },
  { value: "other", label: "Other" },
];

const FREQ = [
  { value: "always", label: "Always" },
  { value: "sometimes", label: "Sometimes" },
  { value: "never", label: "Never" },
];

// ---------- Scoring helper (client preview only) ----------
function computeFnpScorePreview(s) {
  let score = 0;

  const improvedSource = ["piped", "protected_well"].includes(s.waterSource);
  if (improvedSource) score += 2;

  if (s.treatsWater) score += 2;
  if (s.treatmentFrequency === "always") score += 1;

  if (s.storedCovered) score += 1;
  if (s.storageContainerClean) score += 1;
  if (s.separateLadleUsed) score += 1;
  if (s.handwashBeforeServing) score += 1;

  if (s.usesSafeWaterForInfantFood) score += 1;
  if (s.cleansUtensilsProperly) score += 1;

  let grade = "Poor";
  if (score >= 8) grade = "Good";
  else if (score >= 4) grade = "Moderate";

  return { score, grade };
}

// ---------- CSV ----------
function toCSV(rows) {
  const headers = [
    "surveyDate",
    "masterSiteName",
    "siteName",
    "respondentName",
    "respondentPhone",
    "householdId",
    "waterSource",
    "treatsWater",
    "treatmentMethod",
    "treatmentFrequency",
    "storedCovered",
    "storageContainerClean",
    "separateLadleUsed",
    "handwashBeforeServing",
    "usesSafeWaterForInfantFood",
    "cleansUtensilsProperly",
    "score",
    "grade",
    "notes",
  ];

  const esc = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    if (s.includes(",") || s.includes('"') || s.includes("\n")) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const lines = [
    headers.join(","),
    ...rows.map((r) =>
      headers
        .map((h) => {
          if (h === "surveyDate") return esc(r.surveyDate ? new Date(r.surveyDate).toISOString().slice(0, 10) : "");
          return esc(r[h]);
        })
        .join(",")
    ),
  ];

  return lines.join("\n");
}

function downloadTextFile(filename, content, mime = "text/csv;charset=utf-8") {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function FnpWaterModule() {
  const { user } = useContext(AuthContext);
  const token = localStorage.getItem("token");

  const isAdmin =
    user?.role === "admin" ||
    user?.role === "super-admin" ||
    user?.role === "Manager";

  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  // ---------- masterSites & sites ----------
  const [masterSites, setMasterSites] = useState([]);
  const [sites, setSites] = useState([]);

  // ---------- filters ----------
  const [filterMasterSiteId, setFilterMasterSiteId] = useState("");
  const [filterSiteId, setFilterSiteId] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [filterGrade, setFilterGrade] = useState("");

  // ---------- data ----------
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ byGrade: [] });

  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });

  // ---------- form ----------
  const [formOpen, setFormOpen] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const emptyForm = {
    _id: null,
    masterSiteId: "",
    siteId: "",
    surveyDate: new Date().toISOString().slice(0, 10),
    respondentName: "",
    respondentPhone: "",
    householdId: "",
    waterSource: "piped",
    treatsWater: false,
    treatmentMethod: "none",
    treatmentFrequency: "never",
    storedCovered: true,
    storageContainerClean: true,
    separateLadleUsed: true,
    handwashBeforeServing: true,
    usesSafeWaterForInfantFood: false,
    cleansUtensilsProperly: true,
    notes: "",
  };

  const [form, setForm] = useState(emptyForm);

  const preview = useMemo(() => computeFnpScorePreview(form), [form]);

  // keep form treatment fields consistent
  useEffect(() => {
    if (!form.treatsWater) {
      if (form.treatmentMethod !== "none") setForm((p) => ({ ...p, treatmentMethod: "none" }));
      if (form.treatmentFrequency !== "never") setForm((p) => ({ ...p, treatmentFrequency: "never" }));
    } else {
      if (form.treatmentMethod === "none") setForm((p) => ({ ...p, treatmentMethod: "boil" }));
      if (form.treatmentFrequency === "never") setForm((p) => ({ ...p, treatmentFrequency: "sometimes" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.treatsWater]);

  // ---------- load masterSites ----------
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(API.mastersites, authHeader);
        setMasterSites(res.data || []);
      } catch (e) {
        setMsg({ type: "err", text: "Failed to load MasterSites." });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- load sites when masterSite filter changes ----------
  useEffect(() => {
    (async () => {
      try {
        if (!filterMasterSiteId && !form.masterSiteId) {
          // load all sites (optional)
          const res = await axios.get(API.sites, authHeader);
          setSites(res.data || []);
          return;
        }

        // If your backend supports query param:
        const ms = filterMasterSiteId || form.masterSiteId;
        const res = await axios.get(`${API.sites}?masterSiteId=${ms}`, authHeader);
        setSites(res.data || []);
      } catch (e) {
        // fallback: ignore
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterMasterSiteId, form.masterSiteId]);

  // ---------- fetch list + summary ----------
  const fetchData = async () => {
    setLoading(true);
    setMsg({ type: "", text: "" });

    try {
      const params = {};
      if (filterMasterSiteId) params.masterSiteId = filterMasterSiteId;
      if (filterSiteId) params.siteId = filterSiteId;
      if (filterFrom) params.from = filterFrom;
      if (filterTo) params.to = filterTo;
      if (filterGrade) params.grade = filterGrade;

      const [listRes, sumRes] = await Promise.all([
        axios.get(API.fnp, { ...authHeader, params }),
        axios.get(API.summary, { ...authHeader, params }),
      ]);

      const list = listRes.data || [];
      const sum = sumRes.data || { byGrade: [] };

      // map names for CSV/table
      const msMap = new Map(masterSites.map((m) => [String(m._id), m.name || m.masterSiteName || "MasterSite"]));
      const sMap = new Map(sites.map((s) => [String(s._id), s.name || s.siteName || "Site"]));

      const withNames = list.map((r) => ({
        ...r,
        masterSiteName: r.masterSiteName || msMap.get(String(r.masterSiteId)) || "",
        siteName: r.siteName || sMap.get(String(r.siteId)) || "",
      }));

      setRows(withNames);
      setSummary(sum);
    } catch (e) {
      setMsg({ type: "err", text: "Failed to fetch FNP data. Check API routes & token." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // initial fetch after mastersites load
    if (masterSites.length) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterSites.length]);

  const resetFilters = () => {
    setFilterMasterSiteId("");
    setFilterSiteId("");
    setFilterFrom("");
    setFilterTo("");
    setFilterGrade("");
    setTimeout(fetchData, 0);
  };

  // ---------- create/update ----------
  const submitForm = async () => {
    if (!form.masterSiteId || !form.siteId) {
      setMsg({ type: "err", text: "Please select MasterSite and Site." });
      return;
    }

    setSubmitting(true);
    setMsg({ type: "", text: "" });

    try {
      if (form._id) {
        await axios.put(`${API.fnp}/${form._id}`, form, authHeader);
        setMsg({ type: "ok", text: "Survey updated." });
      } else {
        await axios.post(API.fnp, form, authHeader);
        setMsg({ type: "ok", text: "Survey saved." });
      }
      setForm(emptyForm);
      await fetchData();
    } catch (e) {
      setMsg({ type: "err", text: "Failed to save survey." });
    } finally {
      setSubmitting(false);
    }
  };

  const editRow = (r) => {
    setFormOpen(true);
    setForm({
      _id: r._id,
      masterSiteId: r.masterSiteId,
      siteId: r.siteId,
      surveyDate: r.surveyDate ? new Date(r.surveyDate).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10),
      respondentName: r.respondentName || "",
      respondentPhone: r.respondentPhone || "",
      householdId: r.householdId || "",
      waterSource: r.waterSource || "piped",
      treatsWater: !!r.treatsWater,
      treatmentMethod: r.treatmentMethod || "none",
      treatmentFrequency: r.treatmentFrequency || "never",
      storedCovered: !!r.storedCovered,
      storageContainerClean: !!r.storageContainerClean,
      separateLadleUsed: !!r.separateLadleUsed,
      handwashBeforeServing: !!r.handwashBeforeServing,
      usesSafeWaterForInfantFood: !!r.usesSafeWaterForInfantFood,
      cleansUtensilsProperly: !!r.cleansUtensilsProperly,
      notes: r.notes || "",
    });

    // keep selection consistent with filter (optional)
    if (!filterMasterSiteId) setFilterMasterSiteId(String(r.masterSiteId || ""));
    if (!filterSiteId) setFilterSiteId(String(r.siteId || ""));
  };

  const deleteRow = async (r) => {
    if (!isAdmin) return;
    const ok = window.confirm("Delete this survey?");
    if (!ok) return;

    try {
      await axios.delete(`${API.fnp}/${r._id}`, authHeader);
      setMsg({ type: "ok", text: "Deleted." });
      await fetchData();
    } catch (e) {
      setMsg({ type: "err", text: "Failed to delete." });
    }
  };

  // ---------- charts ----------
  const kpis = useMemo(() => {
    const count = rows.length;
    const avg = count ? rows.reduce((a, b) => a + (Number(b.score) || 0), 0) / count : 0;

    const gradeCounts = rows.reduce(
      (acc, r) => {
        const g = r.grade || "Poor";
        acc[g] = (acc[g] || 0) + 1;
        return acc;
      },
      { Poor: 0, Moderate: 0, Good: 0 }
    );

    return {
      count,
      avgScore: avg,
      gradeCounts,
    };
  }, [rows]);

  const gradeBarData = useMemo(() => {
    const labels = ["Poor", "Moderate", "Good"];
    const values = labels.map((l) => kpis.gradeCounts[l] || 0);

    return {
      labels,
      datasets: [{ label: "Surveys", data: values }],
    };
  }, [kpis]);

  const trendLineData = useMemo(() => {
    // group by date
    const map = new Map();
    rows.forEach((r) => {
      const d = r.surveyDate ? new Date(r.surveyDate).toISOString().slice(0, 10) : "";
      if (!d) return;
      const cur = map.get(d) || { sum: 0, n: 0 };
      cur.sum += Number(r.score) || 0;
      cur.n += 1;
      map.set(d, cur);
    });

    const labels = Array.from(map.keys()).sort();
    const values = labels.map((d) => {
      const v = map.get(d);
      return v.n ? Number((v.sum / v.n).toFixed(2)) : 0;
    });

    return {
      labels,
      datasets: [{ label: "Avg FNP Score", data: values }],
    };
  }, [rows]);

  const exportCSV = () => {
    const csv = toCSV(rows);
    const name = `fnp_surveys_${new Date().toISOString().slice(0, 10)}.csv`;
    downloadTextFile(name, csv);
  };

  // ---------- derived site lists ----------
  const filteredSitesForFilter = useMemo(() => {
    if (!filterMasterSiteId) return sites;
    return sites.filter((s) => String(s.masterSiteId) === String(filterMasterSiteId));
  }, [sites, filterMasterSiteId]);

  const filteredSitesForForm = useMemo(() => {
    if (!form.masterSiteId) return sites;
    return sites.filter((s) => String(s.masterSiteId) === String(form.masterSiteId));
  }, [sites, form.masterSiteId]);

  return (
    <div className="fnpPage">
      <div className="fnpTop">
        <div className="fnpTitleWrap">
          <h1 className="fnpTitle">FNP — Drinking Water Practices</h1>
          <p className="fnpSub">
            Capture water safety practices (source, treatment, storage) and auto-score FNP for impact tracking.
          </p>
        </div>

        <div className="fnpActions">
          <button className="fnpBtn fnpBtnGhost" onClick={() => setFormOpen((p) => !p)}>
            {formOpen ? "Hide Form" : "Add Survey"}
          </button>
          <button className="fnpBtn fnpBtnPrimary" onClick={fetchData} disabled={loading}>
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      {msg.text ? (
        <div className={`fnpAlert ${msg.type === "err" ? "fnpAlertErr" : "fnpAlertOk"}`}>{msg.text}</div>
      ) : null}

      {/* Filters */}
      <div className="fnpCard">
        <div className="fnpCardHeader">
          <h2 className="fnpCardTitle">Filters</h2>
          <div className="fnpInline">
            <button className="fnpBtn fnpBtnGhost" onClick={resetFilters}>
              Reset
            </button>
            <button className="fnpBtn" onClick={exportCSV} disabled={!rows.length}>
              Export CSV
            </button>
          </div>
        </div>

        <div className="fnpGrid">
          <div className="fnpField">
            <label>MasterSite</label>
            <select
              value={filterMasterSiteId}
              onChange={(e) => {
                setFilterMasterSiteId(e.target.value);
                setFilterSiteId("");
              }}
            >
              <option value="">All MasterSites</option>
              {masterSites.map((m) => (
                <option key={m._id} value={m._id}>
                  {m.name || m.masterSiteName || "MasterSite"}
                </option>
              ))}
            </select>
          </div>

          <div className="fnpField">
            <label>Site</label>
            <select value={filterSiteId} onChange={(e) => setFilterSiteId(e.target.value)}>
              <option value="">All Sites</option>
              {filteredSitesForFilter.map((s) => (
                <option key={s._id} value={s._id}>
                  {s.name || s.siteName || "Site"}
                </option>
              ))}
            </select>
          </div>

          <div className="fnpField">
            <label>From</label>
            <input type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} />
          </div>

          <div className="fnpField">
            <label>To</label>
            <input type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} />
          </div>

          <div className="fnpField">
            <label>Grade</label>
            <select value={filterGrade} onChange={(e) => setFilterGrade(e.target.value)}>
              <option value="">All</option>
              <option value="Poor">Poor</option>
              <option value="Moderate">Moderate</option>
              <option value="Good">Good</option>
            </select>
          </div>

          <div className="fnpField fnpFieldWide">
            <label>&nbsp;</label>
            <button className="fnpBtn fnpBtnPrimary" onClick={fetchData} disabled={loading}>
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      {/* KPI + Charts */}
      <div className="fnpRow2">
        <div className="fnpCard">
          <div className="fnpCardHeader">
            <h2 className="fnpCardTitle">KPIs</h2>
            <span className="fnpPill">Total: {kpis.count}</span>
          </div>

          <div className="fnpKpiGrid">
            <div className="fnpKpi">
              <div className="fnpKpiLabel">Average FNP Score</div>
              <div className="fnpKpiValue">{kpis.avgScore.toFixed(2)}</div>
              <div className="fnpKpiHint">Scale: 0–10</div>
            </div>

            <div className="fnpKpi">
              <div className="fnpKpiLabel">Good</div>
              <div className="fnpKpiValue">{kpis.gradeCounts.Good || 0}</div>
              <div className="fnpKpiHint">Score ≥ 8</div>
            </div>

            <div className="fnpKpi">
              <div className="fnpKpiLabel">Moderate</div>
              <div className="fnpKpiValue">{kpis.gradeCounts.Moderate || 0}</div>
              <div className="fnpKpiHint">Score 4–7</div>
            </div>

            <div className="fnpKpi">
              <div className="fnpKpiLabel">Poor</div>
              <div className="fnpKpiValue">{kpis.gradeCounts.Poor || 0}</div>
              <div className="fnpKpiHint">Score ≤ 3</div>
            </div>
          </div>
        </div>

        <div className="fnpCard">
          <div className="fnpCardHeader">
            <h2 className="fnpCardTitle">Grade Distribution</h2>
          </div>
          <div className="fnpChart">
            <Bar
              data={gradeBarData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: true }, title: { display: false } },
              }}
            />
          </div>
        </div>
      </div>

      <div className="fnpCard">
        <div className="fnpCardHeader">
          <h2 className="fnpCardTitle">Trend</h2>
          <span className="fnpMuted">Average score by survey date</span>
        </div>
        <div className="fnpChart fnpChartTall">
          <Line
            data={trendLineData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: { legend: { display: true }, title: { display: false } },
              scales: { y: { beginAtZero: true, suggestedMax: 10 } },
            }}
          />
        </div>
      </div>

      {/* Form */}
      {formOpen ? (
        <div className="fnpCard">
          <div className="fnpCardHeader">
            <h2 className="fnpCardTitle">{form._id ? "Edit Survey" : "Add Survey"}</h2>
            <div className="fnpInline">
              <span className={`fnpBadge ${preview.grade === "Good" ? "good" : preview.grade === "Moderate" ? "mod" : "poor"}`}>
                Preview: {preview.score}/10 — {preview.grade}
              </span>
              {form._id ? (
                <button className="fnpBtn fnpBtnGhost" onClick={() => setForm(emptyForm)}>
                  Cancel Edit
                </button>
              ) : null}
            </div>
          </div>

          <div className="fnpGrid">
            <div className="fnpField">
              <label>MasterSite *</label>
              <select
                value={form.masterSiteId}
                onChange={(e) => {
                  setForm((p) => ({ ...p, masterSiteId: e.target.value, siteId: "" }));
                }}
              >
                <option value="">Select MasterSite</option>
                {masterSites.map((m) => (
                  <option key={m._id} value={m._id}>
                    {m.name || m.masterSiteName || "MasterSite"}
                  </option>
                ))}
              </select>
            </div>

            <div className="fnpField">
              <label>Site *</label>
              <select
                value={form.siteId}
                onChange={(e) => setForm((p) => ({ ...p, siteId: e.target.value }))}
                disabled={!form.masterSiteId}
              >
                <option value="">Select Site</option>
                {filteredSitesForForm.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.name || s.siteName || "Site"}
                  </option>
                ))}
              </select>
            </div>

            <div className="fnpField">
              <label>Survey Date *</label>
              <input
                type="date"
                value={form.surveyDate}
                onChange={(e) => setForm((p) => ({ ...p, surveyDate: e.target.value }))}
              />
            </div>

            <div className="fnpField">
              <label>Respondent Name</label>
              <input
                value={form.respondentName}
                onChange={(e) => setForm((p) => ({ ...p, respondentName: e.target.value }))}
                placeholder="Optional"
              />
            </div>

            <div className="fnpField">
              <label>Respondent Phone</label>
              <input
                value={form.respondentPhone}
                onChange={(e) => setForm((p) => ({ ...p, respondentPhone: e.target.value }))}
                placeholder="Optional"
              />
            </div>

            <div className="fnpField">
              <label>Household ID</label>
              <input
                value={form.householdId}
                onChange={(e) => setForm((p) => ({ ...p, householdId: e.target.value }))}
                placeholder="Optional"
              />
            </div>

            <div className="fnpField">
              <label>Water Source *</label>
              <select
                value={form.waterSource}
                onChange={(e) => setForm((p) => ({ ...p, waterSource: e.target.value }))}
              >
                {WATER_SOURCES.map((w) => (
                  <option key={w.value} value={w.value}>
                    {w.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="fnpField fnpFieldWide">
              <label className="fnpCheckLabel">
                <input
                  type="checkbox"
                  checked={form.treatsWater}
                  onChange={(e) => setForm((p) => ({ ...p, treatsWater: e.target.checked }))}
                />
                Treats drinking water before use (boil/filter/chlorine/RO etc.)
              </label>
              <div className="fnpInline fnpInlineGap">
                <div className="fnpFieldMini">
                  <label>Treatment Method</label>
                  <select
                    value={form.treatmentMethod}
                    onChange={(e) => setForm((p) => ({ ...p, treatmentMethod: e.target.value }))}
                    disabled={!form.treatsWater}
                  >
                    {TREAT_METHODS.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="fnpFieldMini">
                  <label>Frequency</label>
                  <select
                    value={form.treatmentFrequency}
                    onChange={(e) => setForm((p) => ({ ...p, treatmentFrequency: e.target.value }))}
                    disabled={!form.treatsWater}
                  >
                    {FREQ.map((f) => (
                      <option key={f.value} value={f.value}>
                        {f.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="fnpField fnpFieldWide">
              <div className="fnpChecklist">
                <label className="fnpCheckLabel">
                  <input
                    type="checkbox"
                    checked={form.storedCovered}
                    onChange={(e) => setForm((p) => ({ ...p, storedCovered: e.target.checked }))}
                  />
                  Water stored in a covered container
                </label>

                <label className="fnpCheckLabel">
                  <input
                    type="checkbox"
                    checked={form.storageContainerClean}
                    onChange={(e) => setForm((p) => ({ ...p, storageContainerClean: e.target.checked }))}
                  />
                  Storage container is cleaned regularly
                </label>

                <label className="fnpCheckLabel">
                  <input
                    type="checkbox"
                    checked={form.separateLadleUsed}
                    onChange={(e) => setForm((p) => ({ ...p, separateLadleUsed: e.target.checked }))}
                  />
                  Separate ladle/tap used (no dipping hands/cups)
                </label>

                <label className="fnpCheckLabel">
                  <input
                    type="checkbox"
                    checked={form.handwashBeforeServing}
                    onChange={(e) => setForm((p) => ({ ...p, handwashBeforeServing: e.target.checked }))}
                  />
                  Handwashing before serving drinking water
                </label>

                <label className="fnpCheckLabel">
                  <input
                    type="checkbox"
                    checked={form.usesSafeWaterForInfantFood}
                    onChange={(e) => setForm((p) => ({ ...p, usesSafeWaterForInfantFood: e.target.checked }))}
                  />
                  Safe water used for infant food/formula
                </label>

                <label className="fnpCheckLabel">
                  <input
                    type="checkbox"
                    checked={form.cleansUtensilsProperly}
                    onChange={(e) => setForm((p) => ({ ...p, cleansUtensilsProperly: e.target.checked }))}
                  />
                  Utensils washed properly (soap + safe drying)
                </label>
              </div>
            </div>

            <div className="fnpField fnpFieldWide">
              <label>Notes</label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                placeholder="Optional observations..."
                rows={3}
              />
            </div>

            <div className="fnpField fnpFieldWide fnpFormActions">
              <button className="fnpBtn fnpBtnPrimary" onClick={submitForm} disabled={submitting}>
                {submitting ? "Saving..." : form._id ? "Update Survey" : "Save Survey"}
              </button>
              <button className="fnpBtn" onClick={() => setForm(emptyForm)} disabled={submitting}>
                Clear
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Table */}
      <div className="fnpCard">
        <div className="fnpCardHeader">
          <h2 className="fnpCardTitle">Surveys</h2>
          <span className="fnpMuted">
            Showing {rows.length} record{rows.length === 1 ? "" : "s"}
          </span>
        </div>

        <div className="fnpTableWrap">
          <table className="fnpTable">
            <thead>
              <tr>
                <th>Date</th>
                <th>MasterSite</th>
                <th>Site</th>
                <th>Source</th>
                <th>Treat</th>
                <th>Storage</th>
                <th>Score</th>
                <th>Grade</th>
                <th>Respondent</th>
                <th className="fnpThActions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {!rows.length ? (
                <tr>
                  <td colSpan={10} className="fnpEmpty">
                    No surveys found for selected filters.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r._id}>
                    <td>{r.surveyDate ? new Date(r.surveyDate).toISOString().slice(0, 10) : ""}</td>
                    <td className="fnpStrong">{r.masterSiteName || ""}</td>
                    <td>{r.siteName || ""}</td>
                    <td>{r.waterSource}</td>
                    <td>{r.treatsWater ? `${r.treatmentMethod} (${r.treatmentFrequency})` : "No"}</td>
                    <td>
                      {r.storedCovered ? "Covered" : "Open"} / {r.separateLadleUsed ? "Ladle" : "No ladle"}
                    </td>
                    <td className="fnpScore">{Number(r.score || 0)}</td>
                    <td>
                      <span
                        className={`fnpBadge ${
                          r.grade === "Good" ? "good" : r.grade === "Moderate" ? "mod" : "poor"
                        }`}
                      >
                        {r.grade}
                      </span>
                    </td>
                    <td className="fnpMuted">
                      {(r.respondentName || "-") + (r.respondentPhone ? ` (${r.respondentPhone})` : "")}
                    </td>
                    <td className="fnpActionsCell">
                      <button className="fnpMiniBtn" onClick={() => editRow(r)}>
                        Edit
                      </button>
                      {isAdmin ? (
                        <button className="fnpMiniBtn danger" onClick={() => deleteRow(r)}>
                          Delete
                        </button>
                      ) : (
                        <span className="fnpMuted">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Summary from backend (optional display) */}
        {summary?.byGrade?.length ? (
          <div className="fnpSummaryStrip">
            {summary.byGrade.map((g) => (
              <div key={g._id} className="fnpSummaryItem">
                <span className="fnpSummaryKey">{g._id}</span>
                <span className="fnpSummaryVal">{g.count}</span>
                <span className="fnpSummaryHint">avg {Number(g.avgScore || 0).toFixed(2)}</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
